import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * SIMG-COMMERCIAL-LEADBETON-2026-001 — Primitives UI partagées par le portail.
 *
 * Mêmes patterns que `pages/terrain/_ui.tsx` (Badge + Modal via portail) — on
 * duplique volontairement pour garder chaque module autonome dans son dossier
 * plutôt qu'imposer un import cross-module qui rend la refactorisation difficile.
 */

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

const BADGE_STYLES: Record<BadgeTone, { bg: string; color: string; border: string }> = {
  neutral: { bg: '#F3F4F6', color: '#374151', border: '#D1D5DB' },
  success: { bg: '#ECFDF5', color: '#065F46', border: '#A7F3D0' },
  warning: { bg: '#FFFBEB', color: '#92400E', border: '#FDE68A' },
  danger:  { bg: '#FEF2F2', color: '#991B1B', border: '#FECACA' },
  info:    { bg: '#EFF6FF', color: '#1E40AF', border: '#BFDBFE' },
};

export function Badge({ tone, children }: { tone: BadgeTone; children: ReactNode }) {
  const s = BADGE_STYLES[tone];
  return (
    <span style={{
      display:      'inline-block',
      background:   s.bg,
      color:        s.color,
      border:       `1px solid ${s.border}`,
      borderRadius: 999,
      padding:      '3px 10px',
      fontSize:     11,
      fontWeight:   600,
      whiteSpace:   'nowrap',
      lineHeight:   1.3,
    }}>
      {children}
    </span>
  );
}

export function Modal({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return createPortal(
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        overflow: 'auto', backdropFilter: 'blur(2px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 12,
          boxShadow: '0 20px 60px -12px rgba(0,0,0,0.35)',
          maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 32px)',
          overflow: 'auto',
        }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function ModalHeader({ titre, onClose }: { titre: string; onClose: () => void }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
      <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{titre}</h2>
      <button
        onClick={onClose}
        aria-label="Fermer"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#6B7280' }}
      >
        <X size={20} />
      </button>
    </div>
  );
}
